import { useState, useRef, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useBasePath } from '../../lib/config/nav-helpers';
import { api } from '../../lib/api';
import { clsx } from 'clsx';
import { useLocalStorage } from '../../lib/hooks/use-local-storage';
import { usePreference } from '../../lib/hooks/use-preference';

interface Notification {
  id: string;
  type: 'alert' | 'insight' | 'action';
  title: string;
  body: string;
  time: string;
  read: boolean;
  navPath?: string | null;
}


const TAB_LABELS = ['All', 'Alerts', 'Insights', 'Actions'] as const;
type TabLabel = (typeof TAB_LABELS)[number];

const TYPE_COLORS: Record<string, string> = {
  alert: 'bg-danger',
  insight: 'bg-primary',
  action: 'bg-warn',
};

function getNotificationDestination(n: Notification, basePath: string): string {
  // Use API-provided navPath if available
  if (n.navPath) return `${basePath}${n.navPath}`;
  // Fallback: infer from content
  const text = `${n.title} ${n.body}`.toLowerCase();
  if (/deal|pipeline/.test(text)) return `${basePath}/pipeline`;
  if (/client|health/.test(text)) return `${basePath}/clients`;
  if (/follow-up|action/.test(text)) return `${basePath}/actions`;
  if (/revenue|milestone/.test(text)) return `${basePath}/revenue`;
  return basePath;
}

export function NotificationCenter() {
  const navigate = useNavigate();
  const basePath = useBasePath();

  const [open, setOpen] = useState(false);
  const [tab, setTab] = useLocalStorage<TabLabel>('notifications:tab', 'All');
  const [readIdsList, setReadIdsList] = usePreference<string[]>('notification_read_ids', []);
  const readIds = useMemo(() => new Set(readIdsList), [readIdsList]);
  const ref = useRef<HTMLDivElement>(null);

  // Notifications come from the API in every workspace, demo included.
  const feedQ = useQuery({
    queryKey: ['notifications', 'feed'],
    queryFn: () => api<{ notifications: Notification[]; unreadCount: number }>('/v1/notifications/feed'),
    staleTime: 60_000,       // Refresh every minute
    refetchInterval: 60_000, // Auto-poll
    retry: false,
  });
  const source = (feedQ.data?.notifications ?? []).map(n => ({
    ...n,
    // API returns navPath, map to the notification destination format
    read: n.read || readIds.has(n.id),
  }));
  const notifications = useMemo(
    () => source.map(n => ({ ...n, read: n.read || readIds.has(n.id) })),
    [source, readIds],
  );
  const unread = useMemo(() => notifications.filter(n => !n.read).length, [notifications]);

  const markRead = (id: string) => {
    if (readIds.has(id)) return;
    setReadIdsList(prev => [...prev, id]);
  };
  const markAllRead = () => {
    setReadIdsList(prev => {
      const combined = new Set(prev);
      for (const n of source) combined.add(n.id);
      return [...combined];
    });
  };

  // Show read items only when the user opts in. Default view = unread only,
  // so the count in the badge matches the list 1:1.
  const [showRead, setShowRead] = useLocalStorage('notifications:show-read', false);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const visible = useMemo(
    () => showRead ? notifications : notifications.filter(n => !n.read),
    [notifications, showRead],
  );
  const filtered = useMemo(
    () => tab === 'All' ? visible : visible.filter(n => n.type === tab.toLowerCase().slice(0, -1)),
    [visible, tab],
  );
  const readCount = useMemo(() => notifications.filter(n => n.read).length, [notifications]);

  return (
    <div ref={ref} className="relative">
      <button aria-label={unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'} aria-expanded={open} onClick={() => setOpen(o => !o)} className="relative rounded-lg p-2 text-ink-muted transition-colors hover:bg-surface-2 hover:text-ink">
        <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        {unread > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-danger px-1 text-2xs font-semibold text-on-fill">
            {unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full z-modal mt-2 w-80 overflow-hidden rounded-xl border border-border shadow-xl animate-fade-in glass">
          <div className="flex items-center justify-between border-b border-border px-4 py-3">
            <h3 className="text-sm font-semibold text-ink">Notifications</h3>
            {unread > 0 && (
              <button onClick={markAllRead} className="text-2xs font-medium text-primary hover:text-primary-bright">
                Mark all read
              </button>
            )}
          </div>

          <div className="flex gap-1 border-b border-border px-3 py-2">
            {TAB_LABELS.map(t => (
              <button key={t} onClick={() => setTab(t)} className={clsx('rounded-md px-2.5 py-1 text-2xs font-medium transition-colors', tab === t ? 'bg-primary/10 text-primary' : 'text-ink-dim hover:text-ink')}>
                {t}
              </button>
            ))}
          </div>

          <div className="max-h-72 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="py-8 text-center">
                <p className="text-2xs text-ink-dim">
                  {showRead ? 'No notifications match this filter.' : "You're all caught up."}
                </p>
                {!showRead && readCount > 0 && (
                  <button
                    onClick={() => setShowRead(true)}
                    className="mt-2 text-2xs font-medium text-primary hover:underline"
                  >
                    Show {readCount} read notification{readCount === 1 ? '' : 's'}
                  </button>
                )}
              </div>
            ) : (
              filtered.map(n => (
                <div
                  key={n.id}
                  onClick={() => {
                    markRead(n.id);
                    setOpen(false);
                    navigate(getNotificationDestination(n, basePath));
                  }}
                  className={clsx('flex gap-3 border-b border-border/50 px-4 py-3 transition-colors hover:bg-surface-1 cursor-pointer', !n.read && 'bg-primary/3')}
                >
                  <span className={clsx('mt-1 h-2 w-2 shrink-0 rounded-full', TYPE_COLORS[n.type])} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <span className={clsx('truncate text-2xs font-semibold', n.read ? 'text-ink-muted' : 'text-ink')}>{n.title}</span>
                      <span className="shrink-0 text-2xs text-ink-dim">{n.time}</span>
                    </div>
                    <p className="mt-0.5 text-2xs text-ink-dim leading-relaxed">{n.body}</p>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Footer: show/hide read history */}
          {readCount > 0 && (
            <div className="border-t border-border/50 px-4 py-2 text-center">
              <button
                onClick={() => setShowRead(v => !v)}
                className="text-2xs font-medium text-ink-dim hover:text-ink"
              >
                {showRead
                  ? `Hide read (${readCount})`
                  : `Show read history (${readCount})`}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
